import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  PageHeader, EmptyState, ActionButton, SearchBar,
} from "@/components/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import CitizenshipSelect from "@/components/CitizenshipSelect";
import { PortalStatusIndicator } from "@/components/PortalStatusIndicator";
import { COUNTRIES } from "@/lib/countries";
import { MergeContactsDialog } from "@/components/MergeContactsDialog";

const CONTACT_TYPES = [
  { value: "lead", label: "Lead" },
  { value: "active_client", label: "Active Client" },
  { value: "inactive_client", label: "Inactive Client" },
  { value: "referral_partner", label: "Referral Partner" },
  { value: "external_production_team", label: "External Production Team" },
  { value: "internal_staff", label: "Internal Staff" },
  { value: "other_contact", label: "Other Contact" },
];

const LIFECYCLE_TONES = {
  lead: "info", active_client: "success", inactive_client: "neutral",
  active: "purple", merged: "warning",
};

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const REFERRAL_SOURCES = [
  { value: "web_search", label: "Web Search" },
  { value: "friend", label: "Friend" },
  { value: "social_media", label: "Social Media" },
  { value: "referral", label: "Referral" },
];

const BRANDING_CENTERS = [
  { value: "expat-tax-firm", label: "Expat Tax Firm" },
  { value: "filetaxesfast", label: "File Taxes Fast" },
];

const BLANK_CONTACT = { contact_type: "lead", first_name: "", last_name: "", business_name: "", email: "", mobile_phone: "", whatsapp: "", country_of_residence: "", citizenships: [], lead_source: "", referral_source: "", firm_id: "expat-tax-firm" };

function CreateContactDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(BLANK_CONTACT);
  const [dups, setDups] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => { if (open) { setForm(BLANK_CONTACT); setDups([]); } }, [open]);

  const submit = async (force = false) => {
    setSaving(true);
    try {
      const { data } = await api.post("/crm/contacts", { ...form, force });
      toast.success(`Created ${data.full_name} (${data.client_number})`);
      onOpenChange(false);
      onCreated(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicates) {
        setDups(detail.duplicates);
        toast.warning("Possible duplicate found — review below.");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Could not create contact");
      }
    } finally { setSaving(false); }
  };

  // Derived: sort Active Client before Lead, and flag strong-identifier collisions
  const LIFECYCLE_ORDER = { active_client: 0, inactive_client: 1, prospect: 2, lead: 3 };
  const sortedDups = [...dups].sort((a, b) =>
    (LIFECYCLE_ORDER[a.lifecycle_status] ?? 99) - (LIFECYCLE_ORDER[b.lifecycle_status] ?? 99)
  );
  const hasExact = sortedDups.some(d => d.reasons.some(r => r === "Email match" || r === "Phone match"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="crm-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Contact</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Contact Type</label>
              <Select value={form.contact_type} onValueChange={set("contact_type")}>
                <SelectTrigger data-testid="crm-create-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Branding Center</label>
              <Select value={form.firm_id} onValueChange={set("firm_id")}>
                <SelectTrigger data-testid="crm-create-firm"><SelectValue /></SelectTrigger>
                <SelectContent>{BRANDING_CENTERS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1 flex items-center gap-1" data-testid="crm-firm-hint">
            <Icons.Palette className="h-3 w-3" /> Determines the client's portal &amp; email branding. Residence outside the U.S. → Expat Tax Firm; residence in the U.S. → File Taxes Fast.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" value={form.first_name} onChange={set("first_name")} testId="crm-create-first" />
            <Field label="Last Name" value={form.last_name} onChange={set("last_name")} testId="crm-create-last" />
          </div>
          <Field label="Business Name (optional)" value={form.business_name} onChange={set("business_name")} testId="crm-create-business" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={form.email} onChange={set("email")} testId="crm-create-email" />
            <Field label="Mobile Phone" value={form.mobile_phone} onChange={set("mobile_phone")} testId="crm-create-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Country of Residence</label>
              <Select value={form.country_of_residence || undefined} onValueChange={(v) => setForm((f) => ({ ...f, country_of_residence: v }))}>
                <SelectTrigger data-testid="crm-create-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Citizenship(s)</label>
            <CitizenshipSelect value={form.citizenships} onChange={(v) => setForm((f) => ({ ...f, citizenships: v }))} testidPrefix="crm-create-citizenship" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Referral Source</label>
              <Select value={form.lead_source || undefined} onValueChange={(v) => setForm((f) => ({ ...f, lead_source: v, referral_source: v === "referral" ? f.referral_source : "" }))}>
                <SelectTrigger data-testid="crm-create-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>{REFERRAL_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.lead_source === "referral" && (
              <Field label="Name of Referral" value={form.referral_source} onChange={set("referral_source")} testId="crm-create-referral-name" placeholder="Who referred them?" />
            )}
          </div>
          {form.lead_source === "referral" && (
            <p className="text-[11px] text-slate-400 -mt-1 flex items-center gap-1" data-testid="crm-referral-comp-note">
              <Icons.Gift className="h-3 w-3" /> Tracked for referral compensation.
            </p>
          )}

          {sortedDups.length > 0 && (
            <div
              className={`rounded-lg border p-3 ${hasExact ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
              data-testid="crm-duplicate-warning"
            >
              <div className={`flex items-center gap-2 text-sm font-medium mb-2 ${hasExact ? "text-red-800" : "text-amber-800"}`}>
                <Icons.AlertTriangle className="h-4 w-4" />
                {hasExact
                  ? "Exact match found — record already exists"
                  : sortedDups.length === 1 ? "Possible duplicate found" : `${sortedDups.length} possible duplicates found`}
              </div>
              <div className="space-y-2">
                {sortedDups.map((d) => {
                  const isExactRecord = d.reasons.some(r => r === "Email match" || r === "Phone match");
                  return (
                    <div key={d.id} className="flex items-center justify-between gap-2">
                      <div className="text-xs text-amber-900">
                        {isExactRecord && (
                          <span className="inline-block bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5 rounded mr-1.5 mb-0.5" data-testid={`crm-dup-exact-badge-${d.id}`}>EXACT MATCH</span>
                        )}
                        <span className="font-medium">{d.full_name}</span>
                        {" · "}<span className="text-amber-700">{d.client_number}</span>
                        {" · "}<span className="capitalize text-amber-600">{(d.lifecycle_status || "").replace(/_/g," ")}</span>
                        <span className={`block mt-0.5 ${isExactRecord ? "text-red-600" : "text-amber-500"}`}>{d.reasons.join(", ")}</span>
                      </div>
                      <a href={`/client-workspace/${d.id}`} target="_blank" rel="noreferrer"
                        className="shrink-0 text-xs font-medium text-blue-600 hover:underline whitespace-nowrap"
                        data-testid={`crm-dup-open-${d.id}`}>
                        Open Record →
                      </a>
                    </div>
                  );
                })}
              </div>
              {!hasExact && (
                <p className="text-[11px] text-amber-700 mt-2">Possible duplicate — open the existing record to confirm, or create anyway if you are sure they are different people.</p>
              )}
              {hasExact && (
                <p className="text-[11px] text-red-700 mt-2 font-medium">Exact email or phone match — open the existing record instead of creating a duplicate.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <ActionButton variant="secondary" onClick={() => onOpenChange(false)} data-testid="crm-create-cancel">Cancel</ActionButton>
          {sortedDups.length > 0 && hasExact ? (
            <ActionButton variant="secondary" disabled data-testid="crm-create-blocked"
              title="Exact email or phone match — open the existing record">
              Blocked: Exact Match
            </ActionButton>
          ) : sortedDups.length > 0 ? (
            <ActionButton variant="danger" loading={saving} onClick={() => submit(true)} data-testid="crm-create-force">Create Anyway</ActionButton>
          ) : (
            <ActionButton loading={saving} onClick={() => submit(false)} data-testid="crm-create-submit">Create Contact</ActionButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <input value={value} onChange={onChange} data-testid={testId} placeholder={placeholder}
        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
    </div>
  );
}

export default function CRM({ scope }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = !["read_only"].includes(user?.role);

  const SCOPE_TAB = { leads: "leads", "active-clients": "active", "inactive-clients": "inactive" };
  const SCOPE_TITLE = { leads: "Leads", active: "Active Clients", inactive: "Inactive Clients" };
  const [tab, setTab] = useState(SCOPE_TAB[scope] || "leads");
  useEffect(() => { setTab(SCOPE_TAB[scope] || "leads"); }, [scope]); // eslint-disable-line
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("-last_activity_at");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("crm_page_size")) || 25);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ country: "", priority: "all", assigned_team_id: "all", assigned_case_manager_id: "all", dual_citizen: false, needs_contact_info: false });
  const [showFilters, setShowFilters] = useState(false);
  const [teams, setTeams] = useState([]);
  const [staff, setStaff] = useState([]);
  const [mergeContact, setMergeContact] = useState(null); // contact to merge (opens dialog)

  useEffect(() => { localStorage.setItem("crm_page_size", String(pageSize)); }, [pageSize]);
  useEffect(() => { setPage(0); setSelected(new Set()); }, [tab, query, pageSize, filters]);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab, sort, limit: pageSize, skip: page * pageSize };
      if (query) params.q = query;
      if (filters.country) params.country = filters.country;
      if (filters.priority !== "all") params.priority = filters.priority;
      if (filters.assigned_team_id !== "all") params.assigned_team_id = filters.assigned_team_id;
      if (filters.assigned_case_manager_id !== "all") params.assigned_case_manager_id = filters.assigned_case_manager_id;
      if (filters.dual_citizen) params.dual_citizen = true;
      const [c, s] = await Promise.all([api.get("/crm/contacts", { params }), api.get("/crm/stats")]);
      setRows(c.data?.contacts || []); setTotal(c.data?.total || 0); setStats(s.data);
    } catch (e) { toast.error("Couldn't load contacts."); } finally { setLoading(false); }
  }, [tab, sort, query, page, pageSize, filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleSort = (field) => setSort((s) => (s === `-${field}` ? field : `-${field}`));
  const sortIcon = (field) => sort === `-${field}` ? "ChevronDown" : sort === field ? "ChevronUp" : "ChevronsUpDown";

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const copy = (e, text) => { e.stopPropagation(); navigator.clipboard?.writeText(text); toast.success("Copied"); };

  const runBulk = async (action, extra = {}) => {
    try {
      const { data } = await api.post("/crm/contacts/bulk", { action, ids: [...selected], ...extra });
      toast.success(`Updated ${data.updated} contact(s)`); setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk action failed"); }
  };

  const exportCsv = () => {
    const target = selected.size ? rows.filter((r) => selected.has(r.id)) : rows;
    const addr = (r) => r.mailing_address_structured || {};
    const head = [
      "Name", "Email", "Mobile Phone", "Home Phone", "Primary Phone", "WhatsApp", "LINE",
      "Address Line 1", "Address Line 2", "City", "State / Province / Region", "Postal Code", "Country",
      "Formatted Mailing Address", "Phone", "Mailing Address", "Country of Residence", "Lifecycle", "Client #",
      "Citizenships", "Citizenship Codes", "Dual Citizen",
    ];
    const czNames = (r) => (r.citizenships || []).map((e) => e.country_name || e.country_code).join("; ");
    const czCodes = (r) => (r.citizenships || []).map((e) => e.country_code).join("; ");
    const lines = target.map((r) => [
      r.full_name, r.email, r.mobile_phone, r.home_phone, r.primary_phone, r.whatsapp, r.line_id,
      addr(r).line1, addr(r).line2, addr(r).city, addr(r).region, addr(r).postal_code, addr(r).country,
      r.formatted_mailing_address, r.phone, r.mailing_address, r.country_of_residence, r.lifecycle_stage, r.client_number,
      czNames(r), czCodes(r), r.is_dual_citizen ? "Yes" : "No",
    ].map((v) => `"${(v || "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `crm-${tab}.csv`; a.click();
    toast.success(`Exported ${target.length} contact(s)`);
  };

  const rowAction = async (kind, r) => {
    const cid = r.id;
    if (kind === "workspace") navigate(`/client-workspace/${cid}`);
    else if (kind === "crm") navigate(`/crm/${cid}`);
    else if (kind === "timeline") navigate(`/crm/${cid}?tab=timeline`);
    else if (kind === "message") navigate(`/client-workspace/${cid}?tab=communication`);
    else if (kind === "estimate") navigate(`/sales/estimates?create=1&client=${cid}`);
    else if (kind === "inactive") { await api.patch(`/crm/contacts/${cid}`, { lifecycle_status: "inactive_client" }).then(() => { toast.success("Marked inactive"); load(); }).catch(() => toast.error("Failed")); }
    else if (kind === "merge") setMergeContact(r);
  };

  const tabs = [
    { key: "leads", label: "Leads", count: stats?.leads },
    { key: "active", label: "Active Clients", count: stats?.active_clients },
    { key: "inactive", label: "Inactive Clients", count: stats?.inactive_clients },
  ];
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div data-testid="crm-page">
      <PageHeader title={scope ? SCOPE_TITLE[tab] : "CRM"} description={scope
        ? `${SCOPE_TITLE[tab]} — sorted by most recent activity.`
        : "Master relationship database — sorted by who you're working on right now."} />

      {/* Tabs (legacy only — sub-pages replace these) */}
      {!scope && (
        <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`crm-tab-${t.key}`}
              className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? "text-blue-600" : "text-slate-500 hover:text-slate-700"}`}>
              {t.label}{t.count != null && <span className="ml-1.5 text-xs text-slate-400">{t.count}</span>}
              {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blue-600 rounded-full" />}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {canEdit && <ActionButton icon="UserPlus" onClick={() => setShowCreate(true)} data-testid="crm-new-contact">New Contact</ActionButton>}
          {selected.size > 0 && canEdit && (
            <span className="text-sm text-slate-500" data-testid="crm-selection-count">{selected.size} selected</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBar value={query} onChange={setQuery} placeholder="Search name, email, phone, #…" className="w-full sm:w-72" testId="crm-search" />
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-20" data-testid="crm-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 50, 100, 250].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={exportCsv} data-testid="crm-export" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"><Icons.Download className="h-4 w-4" />Export</button>
          <button onClick={() => setFilters((f) => ({ ...f, dual_citizen: !f.dual_citizen }))} data-testid="crm-dual-citizen-chip"
            className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-2 border transition-colors ${filters.dual_citizen ? "bg-blue-600 text-white border-blue-600" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            <Icons.Users className="h-4 w-4" />Dual citizens
          </button>
          <button onClick={() => setFilters((f) => ({ ...f, needs_contact_info: !f.needs_contact_info }))} data-testid="crm-needs-contact-info-chip"
            className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-2 border transition-colors ${filters.needs_contact_info ? "bg-amber-500 text-white border-amber-500" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            <Icons.MailWarning className="h-4 w-4" />Needs contact info
          </button>
          <button onClick={() => setShowFilters(true)} data-testid="crm-filter-btn" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50"><Icons.SlidersHorizontal className="h-4 w-4" />Filter</button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {canEdit && selected.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3" data-testid="crm-bulk-bar">
          <BulkAssign label="Assign Team" testid="bulk-assign-team" options={teams.map((t) => ({ value: t.id, label: t.name }))} onPick={(v) => runBulk("assign_team", { team_id: v })} />
          <BulkAssign label="Assign Staff" testid="bulk-assign-staff" options={staff.map((s) => ({ value: s.id, label: s.name }))} onPick={(v) => runBulk("assign_staff", { staff_id: v })} />
          <button onClick={() => runBulk("mark_inactive")} data-testid="bulk-inactive" className="text-sm text-slate-600 hover:text-slate-900 px-2 py-1">Mark Inactive</button>
          <button onClick={() => runBulk("archive")} data-testid="bulk-archive" className="text-sm text-slate-600 hover:text-slate-900 px-2 py-1">Archive</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-blue-600 ml-auto px-2 py-1">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="crm-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="pl-5 pr-2 py-3 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="crm-select-all" /></th>
                <th className="px-2 py-3 w-14">Avatar</th>
                <SortableTh label="Name" field="full_name" sort={sort} onSort={toggleSort} icon={sortIcon("full_name")} />
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3 hidden lg:table-cell">Phone</th>
                <SortableTh label="Country" field="country_of_residence" sort={sort} onSort={toggleSort} icon={sortIcon("country_of_residence")} className="hidden md:table-cell" />
                <th className="px-3 py-3">Lifecycle Stage</th>
                <th className="px-3 py-3">Portal</th>
                <SortableTh label="Last Activity" field="last_activity_at" sort={sort} onSort={toggleSort} icon={sortIcon("last_activity_at")} className="hidden xl:table-cell" />
                <th className="px-3 py-3 w-12 text-right">Options</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-16 text-center text-slate-400"><Icons.Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="py-16"><EmptyState icon="Users" title="No contacts" description="Nothing in this tab yet." /></td></tr>
              ) : rows.map((r) => {
                const initials = (r.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <tr key={r.id} data-testid={`crm-row-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors group">
                    <td className="pl-5 pr-2 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`crm-select-${r.id}`} /></td>
                    <td className="px-2 py-3">
                      {r.photo_url
                        ? <img src={`${process.env.REACT_APP_BACKEND_URL}${r.photo_url}?auth=${localStorage.getItem("taxflow_token")}`} alt="" className="h-10 w-10 rounded-full object-cover border border-slate-200" />
                        : <div className="h-10 w-10 rounded-full border border-slate-200 bg-slate-50 text-slate-500 flex items-center justify-center text-xs font-semibold">{initials}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => navigate(`/client-workspace/${r.id}`)} data-testid={`crm-name-${r.id}`} className="text-left">
                        <div className="font-medium text-slate-800 hover:text-blue-600 transition-colors">{r.full_name || "—"}</div>
                        {(r.assigned_team_name || r.assigned_case_manager_name) && (
                          <div className="text-[11px] text-slate-400">{[r.assigned_team_name, r.assigned_case_manager_name].filter(Boolean).join(" · ")}</div>
                        )}
                        {/* Fallback context for incomplete leads missing both email and phone */}
                        {(r.contact_info_status === "incomplete" || (!r.email && !r.mobile_phone && !r.phone && !r.home_phone)) && (() => {
                          const ctx = r.business_name
                            || (r.occupation ? [r.occupation, r.country_of_residence].filter(Boolean).join(" · ") : null)
                            || (r.industry   ? [r.industry,   r.country_of_residence].filter(Boolean).join(" · ") : null)
                            || r.country_of_residence;
                          return ctx ? (
                            <div className="text-[11px] text-slate-400 italic truncate max-w-[180px]" title={ctx} data-testid={`crm-context-${r.id}`}>{ctx}</div>
                          ) : null;
                        })()}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      {r.email ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-600">{r.email}
                          <button onClick={(e) => copy(e, r.email)} data-testid={`crm-copy-${r.id}`} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600"><Icons.Copy className="h-3.5 w-3.5" /></button>
                        </span>) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell text-slate-600">{r.mobile_phone || r.phone || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3 hidden md:table-cell text-slate-600">{r.country_of_residence || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3"><LifecycleBadge stage={r.lifecycle_stage} /></td>
                    <td className="px-3 py-3"><PortalCell r={r} canEdit={canEdit} onChanged={load} /></td>
                    <td className="px-3 py-3 hidden xl:table-cell text-slate-500">{r.last_activity_at ? new Date(r.last_activity_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-3 text-right">
                      <RowMenu r={r} canEdit={canEdit} onAction={rowAction} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span data-testid="crm-pagination-info">{total === 0 ? "0" : `${page * pageSize + 1}–${Math.min(total, (page + 1) * pageSize)}`} of {total}</span>
          <div className="flex items-center gap-1">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="crm-prev" className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><Icons.ChevronLeft className="h-4 w-4" /></button>
            <span className="px-2 text-xs">Page {page + 1} / {lastPage + 1}</span>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} data-testid="crm-next" className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40"><Icons.ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <FilterSheet open={showFilters} onOpenChange={setShowFilters} filters={filters} setFilters={setFilters} teams={teams} staff={staff} />
      <CreateContactDialog open={showCreate} onOpenChange={setShowCreate} onCreated={(d) => navigate(`/client-workspace/${d.id}`)} />
      <MergeContactsDialog
        open={!!mergeContact}
        onOpenChange={(v) => { if (!v) setMergeContact(null); }}
        initialContact={mergeContact}
        onMerged={(survivorId) => { navigate(`/crm/${survivorId}`); }}
      />
    </div>
  );
}

function SortableTh({ label, field, onSort, icon, className = "" }) {
  const I = Icons[icon] || Icons.ChevronsUpDown;
  return (
    <th className={`px-3 py-3 ${className}`}>
      <button onClick={() => onSort(field)} className="inline-flex items-center gap-1 hover:text-slate-600" data-testid={`crm-sort-${field}`}>
        {label}<I className="h-3 w-3" />
      </button>
    </th>
  );
}

const LIFECYCLE_STAGE_TONE = (stage) => {
  const s = (stage || "").toLowerCase();
  if (s === "inactive") return "bg-slate-100 text-slate-500";
  if (s === "completed" || s === "filed / delivered") return "bg-emerald-50 text-emerald-700";
  if (s.includes("waiting")) return "bg-amber-50 text-amber-700";
  if (s.includes("review")) return "bg-purple-50 text-purple-700";
  if (s.includes("production") || s.includes("client success")) return "bg-indigo-50 text-indigo-700";
  if (s.includes("proposal") || s.includes("engagement")) return "bg-blue-50 text-blue-700";
  return "bg-slate-100 text-slate-600";
};
function LifecycleBadge({ stage }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${LIFECYCLE_STAGE_TONE(stage)}`} data-testid="crm-lifecycle-badge">{stage || "—"}</span>;
}

function PortalCell({ r, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);

  const handleAction = async (status) => {
    if (!canEdit || (status !== "not_invited" && status !== "invited")) return;
    if (!r.email) { toast.error("Add an email to this contact first."); return; }
    setBusy(true);
    try {
      const path = status === "invited" ? `/portal/admin/${r.id}/resend` : `/portal/admin/${r.id}/enable`;
      const { data } = await api.post(path);
      const es = data.email_status;
      toast.success(
        (status === "invited" ? "Invitation resent" : "Invitation sent") +
        (es && es !== "sent" && es !== "queued" ? ` (email: ${es})` : "")
      );
      onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invite"); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <PortalStatusIndicator
        client={r}
        onPortalAction={canEdit ? handleAction : undefined}
        busy={busy}
      />
    </div>
  );
}

function RowMenu({ r, canEdit, onAction }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid={`crm-row-menu-${r.id}`}><Icons.MoreVertical className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => onAction("workspace", r)}><Icons.Briefcase className="h-4 w-4 mr-2 text-slate-500" />Open Client Workspace</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("crm", r)}><Icons.Contact className="h-4 w-4 mr-2 text-slate-500" />Open CRM Record</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("message", r)}><Icons.MessageSquare className="h-4 w-4 mr-2 text-slate-500" />Send Message</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("estimate", r)} data-testid={`crm-generate-estimate-${r.id}`}><Icons.Calculator className="h-4 w-4 mr-2 text-blue-500" />Generate Estimate</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction("timeline", r)}><Icons.Clock className="h-4 w-4 mr-2 text-slate-500" />View Timeline</DropdownMenuItem>
        {canEdit && r.lifecycle_status === "lead" && <DropdownMenuItem onClick={() => onAction("inactive", r)}><Icons.UserX className="h-4 w-4 mr-2 text-slate-500" />Mark Inactive</DropdownMenuItem>}
        {canEdit && <DropdownMenuItem onClick={() => onAction("merge", r)} data-testid={`crm-merge-${r.id}`}><Icons.GitMerge className="h-4 w-4 mr-2 text-slate-500" />Merge...</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BulkAssign({ label, options, onPick, testid }) {
  return (
    <Select onValueChange={onPick}>
      <SelectTrigger className="w-40 h-8 bg-white" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function FilterSheet({ open, onOpenChange, filters, setFilters, teams, staff }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="crm-filter-sheet">
        <SheetHeader><SheetTitle className="font-heading">Filters</SheetTitle>
          <SheetDescription>Narrow the contact list by country, priority, team or staff.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-5">
          <div>
            <label className="text-xs font-medium text-slate-500">Country</label>
            <input value={filters.country} onChange={(e) => set("country", e.target.value)} data-testid="filter-country"
              className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="e.g. Thailand" />
          </div>
          <FilterSelect label="Priority" value={filters.priority} onChange={(v) => set("priority", v)} testid="filter-priority"
            options={[["all", "Any"], ["high", "High"], ["medium", "Medium"], ["normal", "Normal"], ["low", "Low"]]} />
          <FilterSelect label="Assigned Team" value={filters.assigned_team_id} onChange={(v) => set("assigned_team_id", v)} testid="filter-team"
            options={[["all", "Any team"], ...teams.map((t) => [t.id, t.name])]} />
          <FilterSelect label="Assigned Staff" value={filters.assigned_case_manager_id} onChange={(v) => set("assigned_case_manager_id", v)} testid="filter-staff"
            options={[["all", "Any staff"], ...staff.map((s) => [s.id, s.name])]} />
          <button onClick={() => setFilters({ country: "", priority: "all", assigned_team_id: "all", assigned_case_manager_id: "all" })}
            data-testid="filter-clear" className="text-sm text-blue-600">Clear all filters</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
